import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import CompanyServices from "services/CompanyService";
import TableServices from "services/TableService";
import PublicMenuServices from "services/PublicMenuService";
import OfferServices from "services/OfferService";
import CustomerServices from "services/CustomerService";
import GuestPortalServices from "services/GuestPortalService";
import GuestRequestServices from "services/GuestRequestService";
import {
  flattenMenuItems,
  GUEST_SERVICES,
  mergeGuestPortalConfig,
} from "data/guestPortalDefaults";
import {
  DUMMY_COMPANY,
  DUMMY_MENU,
  DUMMY_OFFERS,
  dummyTableFor,
  withTimeout,
} from "data/guestPortalDummyData";
import { isValidPhoneNumber, sanitizePhoneNumber } from "utils/phoneUtils";
import { toast } from "react-toastify";

const GuestSessionContext = createContext(null);

const sessionKeyFor = (companyId, tableId) =>
  `guestPortal.session.${companyId || "none"}.${tableId || "none"}`;

const readSession = (key) => {
  try {
    return JSON.parse(window.sessionStorage.getItem(key) || "null");
  } catch {
    return null;
  }
};

const extractRecord = (response) =>
  response?.data?.view?.data ?? response?.data?.view ?? response?.data ?? null;

export const GuestSessionProvider = ({ children }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("companyId") || "";
  const tableId = searchParams.get("tableId") || "";
  const typeOverride = searchParams.get("guestType") || searchParams.get("type");

  const storageKey = sessionKeyFor(companyId, tableId);

  const [loading] = useState(false);
  const [company, setCompany] = useState(DUMMY_COMPANY);
  const [table, setTable] = useState(() => dummyTableFor(tableId, typeOverride));
  const [portalConfig, setPortalConfig] = useState(mergeGuestPortalConfig());
  const [menuData, setMenuData] = useState(DUMMY_MENU);
  const [offerData, setOfferData] = useState(DUMMY_OFFERS);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [enteredMobile, setEnteredMobile] = useState("");
  const [customerSkipped, setCustomerSkipped] = useState(false);
  const [customerPromptOpen, setCustomerPromptOpen] = useState(false);
  const [customerMobileInput, setCustomerMobileInput] = useState("");
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [dnd, setDndState] = useState(false);
  const [makeupRoom, setMakeupRoomState] = useState(false);
  const [requestTick, setRequestTick] = useState(0);

  useEffect(() => {
    const stored = readSession(storageKey);
    if (stored) {
      setCustomerProfile(stored.customerProfile || null);
      setEnteredMobile(stored.enteredMobile || "");
      setCustomerSkipped(!!stored.customerSkipped);
      setDndState(!!stored.dnd);
      setMakeupRoomState(!!stored.makeupRoom);
    } else {
      setCustomerProfile(null);
      setEnteredMobile("");
      setCustomerSkipped(false);
      setDndState(false);
      setMakeupRoomState(false);
    }
  }, [storageKey]);

  useEffect(() => {
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        customerProfile,
        enteredMobile,
        customerSkipped,
        dnd,
        makeupRoom,
      })
    );
  }, [storageKey, customerProfile, enteredMobile, customerSkipped, dnd, makeupRoom]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!companyId) return;

      const [companyRes, tableRes, configRes, menuRes, offerRes] =
        await Promise.allSettled([
          withTimeout(CompanyServices.getById(companyId)),
          tableId ? withTimeout(TableServices.getPublicById(tableId, companyId)) : Promise.resolve(null),
          withTimeout(GuestPortalServices.getConfig(companyId)),
          withTimeout(PublicMenuServices.getMenuByCompanyId(companyId)),
          withTimeout(OfferServices.getByCompanyId(companyId)),
        ]);

      if (cancelled) return;

      const liveCompany =
        companyRes.status === "fulfilled" ? extractRecord(companyRes.value) : null;
      if (liveCompany?.name) {
        setCompany(liveCompany);
      }

      let nextTable = dummyTableFor(tableId, typeOverride);
      if (tableRes.status === "fulfilled" && tableRes.value) {
        const liveTable = extractRecord(tableRes.value);
        if (liveTable && (liveTable.id || liveTable.tableNumber)) {
          nextTable = {
            ...nextTable,
            ...liveTable,
            type: liveTable.type || nextTable.type,
            tableNumber: liveTable.tableNumber || nextTable.tableNumber,
          };
        }
      }
      if (typeOverride && ["table", "room"].includes(typeOverride)) {
        nextTable = { ...nextTable, type: typeOverride };
      }
      setTable(nextTable);

      if (configRes.status === "fulfilled" && configRes.value?.config) {
        setPortalConfig(configRes.value.config);
      }

      if (menuRes.status === "fulfilled") {
        const view = menuRes.value?.data?.view;
        if (Array.isArray(view) && view.length > 0) {
          setMenuData(view);
        }
      }

      if (offerRes.status === "fulfilled") {
        const data =
          offerRes.value?.data?.view?.data ||
          offerRes.value?.data?.view ||
          [];
        if (Array.isArray(data) && data.length > 0) {
          setOfferData(data);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, tableId, typeOverride]);

  const isRoom = (table?.type || "room") !== "table";
  const locationNumber = String(table?.tableNumber || "")
    .trim()
    .replace(/^(room|table)\s+/i, "");
  const locationLabel = locationNumber
    ? `${isRoom ? "Room" : "Table"} ${locationNumber}`
    : isRoom
      ? "Your room"
      : "Your table";

  const visibleServices = useMemo(() => {
    return GUEST_SERVICES.filter((service) => {
      if (isRoom && !service.room) return false;
      if (!isRoom && !service.table) return false;
      if (portalConfig.modules?.[service.moduleKey] === false) return false;
      return true;
    });
  }, [isRoom, portalConfig.modules]);

  const menuItems = useMemo(() => flattenMenuItems(menuData), [menuData]);

  const requests = useMemo(
    () => GuestRequestServices.list({ companyId, tableId }),
    [companyId, tableId, requestTick]
  );

  const refreshRequests = useCallback(() => {
    setRequestTick((value) => value + 1);
  }, []);

  const createRequest = useCallback(
    (payload) => {
      const created = GuestRequestServices.create({
        companyId,
        tableId,
        locationLabel,
        ...payload,
      });
      refreshRequests();
      return created;
    },
    [companyId, tableId, locationLabel, refreshRequests]
  );

  const cancelRequest = useCallback(
    (id) => {
      GuestRequestServices.cancel(id);
      refreshRequests();
    },
    [refreshRequests]
  );

  const addFoodOrder = useCallback(
    (order) => {
      GuestRequestServices.addFoodOrder({
        companyId,
        tableId,
        locationLabel,
        orderId: order?.id,
        orderNo: order?.orderNo || order?.orderNumber,
        title: `Food order ${order?.orderNo || order?.orderNumber || ""}`.trim(),
        details: locationLabel,
      });
      refreshRequests();
    },
    [companyId, tableId, locationLabel, refreshRequests]
  );

  const setDnd = useCallback(
    (active) => {
      setDndState(active);
      GuestRequestServices.upsertToggle({
        companyId,
        tableId,
        type: "dnd",
        title: "Do not disturb",
        details: active ? "Guest requested privacy" : "",
        locationLabel,
        active,
      });
      refreshRequests();
    },
    [companyId, tableId, locationLabel, refreshRequests]
  );

  const setMakeupRoom = useCallback(
    (active) => {
      setMakeupRoomState(active);
      GuestRequestServices.upsertToggle({
        companyId,
        tableId,
        type: "makeup-room",
        title: "Make up room",
        details: active ? "Please tidy the room" : "",
        locationLabel,
        active,
      });
      refreshRequests();
    },
    [companyId, tableId, locationLabel, refreshRequests]
  );

  const skipCustomer = useCallback(() => {
    setCustomerSkipped(true);
    setCustomerPromptOpen(false);
  }, []);

  const lookupCustomer = useCallback(async () => {
    const mobile = sanitizePhoneNumber(customerMobileInput);
    if (!mobile) {
      toast.warning("Please enter a mobile number or skip");
      return;
    }
    if (!isValidPhoneNumber(mobile)) {
      toast.warning("Please enter a valid mobile number (10 to 15 digits)");
      return;
    }
    if (!companyId) {
      toast.error("Hotel information is missing");
      return;
    }

    try {
      setCustomerLookupLoading(true);
      const response = await CustomerServices.getByMobile(companyId, mobile);
      const list = response?.data?.view?.data || response?.data?.view || [];
      const matchedCustomer = Array.isArray(list)
        ? list.find((c) => String(c.mobile || "") === mobile)
        : null;

      setEnteredMobile(mobile);
      if (matchedCustomer) {
        setCustomerProfile(matchedCustomer);
        toast.success(`Welcome back, ${matchedCustomer.name || "Guest"}!`);
      } else {
        setCustomerProfile({
          name: "Guest",
          mobile,
          Loyalty: null,
          Orders: [],
          isGuestOnly: true,
        });
        toast.info("Number saved. Continue as guest.");
      }
      setCustomerPromptOpen(false);
    } catch (error) {
      console.error("Guest customer lookup failed:", error);
      toast.error("Unable to check that number. You can skip and continue.");
    } finally {
      setCustomerLookupLoading(false);
    }
  }, [companyId, customerMobileInput]);

  const guestSearch = location.search;

  const value = {
    loading,
    companyId,
    tableId,
    company,
    table,
    isRoom,
    locationLabel,
    portalConfig,
    menuData,
    menuItems,
    offerData,
    visibleServices,
    guestSearch,
    customerProfile,
    enteredMobile,
    customerSkipped,
    customerPromptOpen,
    customerMobileInput,
    customerLookupLoading,
    setCustomerMobileInput,
    skipCustomer,
    lookupCustomer,
    dnd,
    setDnd,
    makeupRoom,
    setMakeupRoom,
    requests,
    createRequest,
    cancelRequest,
    addFoodOrder,
    refreshRequests,
  };

  return (
    <GuestSessionContext.Provider value={value}>
      {children}
    </GuestSessionContext.Provider>
  );
};

export const useOptionalGuestSession = () => useContext(GuestSessionContext);

export const useGuestSession = () => {
  const ctx = useContext(GuestSessionContext);
  if (!ctx) {
    throw new Error("useGuestSession must be used inside GuestSessionProvider");
  }
  return ctx;
};
